import {
    Box,
    Button,
    Dialog,
    Divider,
    Tab,
    Tabs,
    Typography,
    List,
    ListItemButton,
    ListItemText,
    Chip,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { PosContext } from "../../../shared/PosContext";
import { useGetDocTypeList } from "../../../shared/hooks/useDocTypesData";
import { dateFormat, dateTimeFormat } from "../../../utils/dateUtils";
import {
    getOrderShortName,
    parseAddress,
    parseCustomerAddress,
} from "../../../utils/floorUtils";
import CustomerDialog from "./CustomerDialog/CustomerDialog";
import NewAddressDialog from "./CustomerDialog/NewAddressDialog";
import OrderDetailsDialog from "./OrderDetailsDialog";

function TabPanel({ value, index, children }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function CustomerManagementDialog({
    open,
    onClose,
    selectedCustomer,
    selectedAddress,
    selectedOrder,
    onApplyCustomerChange,
    initialTab = 0,
    autoApplyOnSelect = true,
    isTakeawayOverride,
}) {
    const { customers, addresses } = useContext(PosContext);

    const [tab, setTab] = useState(initialTab);
    const [localCustomer, setLocalCustomer] = useState(selectedCustomer || "");
    const [localAddress, setLocalAddress] = useState(selectedAddress || "");

    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [newAddressDialogOpen, setNewAddressDialogOpen] = useState(false);
    const [historyOrderDetailsOpen, setHistoryOrderDetailsOpen] =
        useState(false);
    const [historyOrderName, setHistoryOrderName] = useState(null);

    useEffect(() => {
        if (open) {
            setTab(initialTab);
            setLocalCustomer(selectedCustomer || "");
            setLocalAddress(selectedAddress || "");
            setCustomerDialogOpen(false);
        }
    }, [open, initialTab, selectedCustomer, selectedAddress]);

    const customerObj = useMemo(() => {
        if (!localCustomer || !customers) return null;
        return (
            customers.find((c) => c.name === localCustomer) ||
            customers.find((c) => c.customer_name === localCustomer) ||
            null
        );
    }, [customers, localCustomer]);

    const addressOptions = useMemo(() => {
        if (!localCustomer || !addresses) return [];
        return addresses[localCustomer] || [];
    }, [addresses, localCustomer]);

    const selectedAddressPhone = useMemo(() => {
        if (!localAddress) return "";
        const selected = addressOptions.find((a) => a.name === localAddress);
        return (
            selected?.phone ||
            selected?.phone_no ||
            selected?.mobile_no ||
            selected?.mobile ||
            ""
        );
    }, [addressOptions, localAddress]);

    const { data: customerOrders, isLoading: isLoadingOrders } =
        useGetDocTypeList({
            doctype: "Table Order",
            filters: localCustomer
                ? [["customer", "=", localCustomer]]
                : [["name", "=", "__none__"]],
            fields: ["name", "creation", "amount", "status"],
            sortBy: "creation desc",
            limit: 20,
        });

    const handleClose = () => onClose?.();

    const openCustomerSelector = () => setCustomerDialogOpen(true);
    const closeCustomerSelector = () => setCustomerDialogOpen(false);
    const openNewAddressDialog = () => setNewAddressDialogOpen(true);
    const closeNewAddressDialog = () => setNewAddressDialogOpen(false);

    const applyChanges = () => {
        if (!selectedOrder?.name) return;
        if (!localCustomer) return;
        onApplyCustomerChange?.(localCustomer, localAddress || null);
        onClose?.();
    };

    const onCustomerSelected = (customerName, addressName) => {
        setLocalCustomer(customerName);
        setLocalAddress(addressName || "");
        setCustomerDialogOpen(false);

        if (autoApplyOnSelect && selectedOrder?.name && customerName) {
            onApplyCustomerChange?.(customerName, addressName || null);
            onClose?.();
        }
    };

    const onHistoryOrderClick = (orderName) => {
        setHistoryOrderName(orderName);
        setHistoryOrderDetailsOpen(true);
    };

    const closeHistoryDetails = () => setHistoryOrderDetailsOpen(false);

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 900 }}>
                                Customer
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#6E7187",
                                    fontWeight: 700,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {localCustomer || "—"}
                            </Typography>
                        </Box>
                        <Button variant="outlined" onClick={handleClose}>
                            Close
                        </Button>
                    </Box>

                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        sx={{ mt: 1 }}
                    >
                        <Tab label="Customer details" />
                        <Tab label="Orders history" />
                    </Tabs>

                    <Divider sx={{ mt: 1 }} />

                    <TabPanel value={tab} index={0}>
                        {!localCustomer ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Typography
                                    sx={{ color: "#6E7187", fontWeight: 700 }}
                                >
                                    No customer selected.
                                </Typography>
                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={openCustomerSelector}
                                    >
                                        Choose customer
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 900 }}>
                                        {customerObj?.customer_name ||
                                            localCustomer}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={openCustomerSelector}
                                    >
                                        Change customer
                                    </Button>
                                </Box>
                                <Typography sx={{ fontWeight: 900 }}>
                                    Details
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#6E7187",
                                        fontWeight: 700,
                                    }}
                                >
                                    Phone:{" "}
                                    {selectedAddressPhone ||
                                        customerObj?.mobile_no ||
                                        "—"}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#6E7187",
                                        fontWeight: 700,
                                    }}
                                >
                                    Address:{" "}
                                    {customerObj
                                        ? parseCustomerAddress(customerObj)
                                        : "—"}
                                </Typography>
                                <Divider sx={{ my: 1 }} />

                                <Typography sx={{ fontWeight: 900 }}>
                                    Address / Phone
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button
                                        variant="outlined"
                                        disabled={!localCustomer}
                                        onClick={openNewAddressDialog}
                                    >
                                        Add address / phone
                                    </Button>
                                </Box>
                                {addressOptions.length > 0 ? (
                                    <List dense>
                                        {addressOptions.map((a) => {
                                            const phone =
                                                a.phone ||
                                                a.phone_no ||
                                                a.mobile_no ||
                                                a.mobile ||
                                                "";
                                            const primary = parseAddress(a) || a.name;
                                            const secondary = phone ? `Phone: ${phone}` : undefined;
                                            return (
                                                <ListItemButton
                                                    key={a.name}
                                                    selected={localAddress === a.name}
                                                    onClick={() => {
                                                        setLocalAddress(a.name);
                                                        if (
                                                            autoApplyOnSelect &&
                                                            selectedOrder?.name &&
                                                            localCustomer
                                                        ) {
                                                            onApplyCustomerChange?.(
                                                                localCustomer,
                                                                a.name
                                                            );
                                                            onClose?.();
                                                        }
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={primary}
                                                        secondary={secondary}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                ) : (
                                    <Typography
                                        sx={{ color: "#6E7187", fontWeight: 700 }}
                                    >
                                        No saved addresses for this customer.
                                    </Typography>
                                )}

                                {localAddress ? (
                                    <Box sx={{ mt: 0.5 }}>
                                        {(() => {
                                            const selected = addressOptions.find(
                                                (a) => a.name === localAddress
                                            );
                                            const selectedPhone =
                                                selected?.phone ||
                                                selected?.phone_no ||
                                                selected?.mobile_no ||
                                                selected?.mobile ||
                                                "";
                                            return (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: "#6E7187",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    Selected:{" "}
                                                    {parseAddress(selected) || localAddress}
                                                    {selectedPhone
                                                        ? ` • ${selectedPhone}`
                                                        : ""}
                                                </Typography>
                                            );
                                        })()}
                                    </Box>
                                ) : null}
                            </Box>
                        )}
                    </TabPanel>

                    <TabPanel value={tab} index={1}>
                        {!localCustomer ? (
                            <Typography
                                sx={{ color: "#6E7187", fontWeight: 700 }}
                            >
                                No customer selected.
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#6E7187",
                                        fontWeight: 800,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Recent orders
                                </Typography>
                                <List dense>
                                    {!isLoadingOrders &&
                                        (customerOrders || []).map((o) => {
                                            const created = o.creation || "";
                                            const createdLabel = created
                                                ? `${dateFormat(created)
                                                      .split("/")
                                                      .slice(0, 2)
                                                      .join("/")} - ${dateTimeFormat(
                                                      created.split(" ")[1] ||
                                                          ""
                                                  )}`
                                                : "";
                                            return (
                                                <ListItemButton
                                                    key={o.name}
                                                    onClick={() =>
                                                        onHistoryOrderClick(
                                                            o.name
                                                        )
                                                    }
                                                >
                                                    <ListItemText
                                                        primary={`#${getOrderShortName(
                                                            o.name
                                                        )} • ${o.amount || 0}₪`}
                                                        secondary={`${createdLabel}${
                                                            o.status
                                                                ? ` • ${o.status}`
                                                                : ""
                                                        }`}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        label="View"
                                                        sx={{
                                                            fontWeight: 800,
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    {isLoadingOrders ? (
                                        <Typography
                                            sx={{
                                                mt: 1,
                                                color: "#6E7187",
                                            }}
                                        >
                                            Loading…
                                        </Typography>
                                    ) : null}
                                </List>
                            </>
                        )}
                    </TabPanel>
                </Box>
            </Dialog>

            <CustomerDialog
                dialogOpen={customerDialogOpen}
                onCancel={closeCustomerSelector}
                onConfirm={onCustomerSelected}
                selectedCustomer={localCustomer}
                customerSelected={onCustomerSelected}
                isTakeawayOverride={isTakeawayOverride}
            />

            <NewAddressDialog
                open={newAddressDialogOpen}
                onClose={closeNewAddressDialog}
                customerName={localCustomer}
                onCreated={(addressDoc) => {
                    const addressName = addressDoc?.name;
                    if (!localCustomer || !addressName) return;
                    setLocalAddress(addressName);
                    closeNewAddressDialog();
                    if (autoApplyOnSelect && selectedOrder?.name) {
                        onApplyCustomerChange?.(localCustomer, addressName);
                        onClose?.();
                    }
                }}
            />

            <OrderDetailsDialog
                isOpen={historyOrderDetailsOpen}
                selectedCustomer={localCustomer}
                title={"Order details"}
                toggle={closeHistoryDetails}
                orderName={historyOrderName}
            />
        </>
    );
}


